@let preview = store.livePreview;
@let heldBack = store.livePreviewHeldBack;
<section class="import-live-preview" aria-labelledby="import-live-preview-heading">
  <div class="import-live-preview__head">
    <h4 id="import-live-preview-heading">{{ 'TRANS__IMPORT__PREVIEW' | translate }}</h4>
    <div class="import-live-preview__stepper">
      <button
        mat-icon-button
        type="button"
        class="import-live-preview__previous"
        [disabled]="store.livePreviewRow <= 1"
        [matTooltip]="'TRANS__IMPORT__LIVE_PREVIEW__PREVIOUS_ROW' | translate"
        [attr.aria-label]="'TRANS__IMPORT__LIVE_PREVIEW__PREVIOUS_ROW' | translate"
        (click)="store.setLivePreviewRow(store.livePreviewRow - 1)"
      >
        <mat-icon>chevron_left</mat-icon>
      </button>
      <span>{{ 'TRANS__IMPORT__LIVE_PREVIEW__ROW' | translate }}</span>
      <mat-form-field
        appearance="outline"
        subscriptSizing="dynamic"
        class="import-live-preview__row-field"
        [style.--import-row-digits]="rowBoxDigits"
      >
        <input
          #rowInput
          matInput
          type="number"
          min="1"
          class="import-live-preview__row-input"
          [attr.max]="store.livePreviewRowCount?.exact ? store.livePreviewRowCount!.rows : null"
          [value]="store.livePreviewRow"
          [attr.aria-label]="'TRANS__IMPORT__LIVE_PREVIEW__ROW' | translate"
          (change)="onRowTyped(rowInput)"
        >
      </mat-form-field>
      @if (store.livePreviewRowCount; as count) {
        <span class="import-live-preview__row-count">
          {{ 'TRANS__IMPORT__LIVE_PREVIEW__OF_ROWS' | translate: {count: rowCountText(count)} }}
        </span>
      }
      <button
        mat-icon-button
        type="button"
        class="import-live-preview__next"
        [disabled]="atLastRow"
        [matTooltip]="'TRANS__IMPORT__LIVE_PREVIEW__NEXT_ROW' | translate"
        [attr.aria-label]="'TRANS__IMPORT__LIVE_PREVIEW__NEXT_ROW' | translate"
        (click)="store.setLivePreviewRow(store.livePreviewRow + 1)"
      >
        <mat-icon>chevron_right</mat-icon>
      </button>
    </div>
  </div>

  @if (store.livePreviewPending) {
    <mat-progress-bar mode="indeterminate" class="import-live-preview__progress"></mat-progress-bar>
  }

  @if (store.livePreviewError; as error) {
    <div class="import-live-preview__error">
      <p>{{ error }}</p>
      <button mat-stroked-button type="button" color="primary" (click)="store.retryLivePreview()">
        <mat-icon>refresh</mat-icon>
        <span class="bold uppercase">{{ 'TRANS__IMPORT__LIVE_PREVIEW__RETRY' | translate }}</span>
      </button>
    </div>
  }

  <!-- What the preview does not show: the answer's errors, and the cards
       it asks for less of than they send, which are listed without an
       answer too. -->
  @let errors = preview?.errors;
  @if (heldBack.length || errors?.fields.length || errors?.messages.length || errors?.mediaPattern
       || errors?.incompleteCards.length || errors?.lackingRights.length) {
    <div class="import-live-preview__answer-errors">
      <p class="bold">{{ 'TRANS__IMPORT__LIVE_PREVIEW__ANSWER_ERRORS' | translate }}</p>
      <ul>
        @for (field of errors?.fields; track field) {
          <li>{{ store.labelFor(field) }}</li>
        }
        @for (message of errors?.messages; track $index) {
          <li>{{ message }}</li>
        }
        @if (errors?.mediaPattern; as mediaPattern) {
          <li>{{ store.mediaPatternText(mediaPattern) }}</li>
        }
        @for (entry of errors?.incompleteCards; track entry.card) {
          <li>{{ store.incompleteCardText(entry) }}</li>
        }
        @for (entry of heldBack; track entry.card) {
          <li>{{ store.incompleteCardText(entry) }}</li>
        }
        @for (entry of errors?.lackingRights; track entry.action) {
          <li>{{ store.lackingRightText(entry) }}</li>
        }
      </ul>
    </div>
  }

  @if (preview) {
    <div class="import-live-preview__body" [class.import-live-preview__body--pending]="store.livePreviewPending">
      @if (!preview.found) {
        <p class="import-live-preview__hint">
          {{ 'TRANS__IMPORT__LIVE_PREVIEW__NO_ROW' | translate: {row: preview.row} }}
        </p>
      } @else {
        <div class="import-live-preview__identity">
          <span class="import-live-preview__identifier">
            @if (preview.identifier) {
              {{ preview.identifier }} ·
            }
            {{ (store.selectedObjectType?.name ?? '') | translate }}
          </span>
          @if (preview.title; as title) {
            <span class="import-live-preview__title">{{ title.text }}</span>
            @if (title.details.length) {
              <span class="import-live-preview__title-details">{{ title.details.join(' · ') }}</span>
            }
          }
        </div>

        @for (section of preview.sections; track section.key) {
          <section class="import-live-preview__section">
            @if (section.titleKey) {
              <h6 class="import-live-preview__section-title">{{ section.titleKey | translate }}</h6>
            }
            <dl class="import-live-preview__fields">
              @for (entry of section.entries; track $index) {
                <div class="import-live-preview__field" [class.import-live-preview__field--wide]="entry.wide">
                  <dt><ng-container *ngTemplateOutlet="labelText; context: {$implicit: entry.label}"></ng-container></dt>
                  @for (value of entry.values; track $index) {
                    <dd class="import-live-preview__value">
                      <ng-container *ngTemplateOutlet="valueText; context: {$implicit: value}"></ng-container>
                      @if (value.path) {
                        <span class="import-live-preview__path">{{ value.path }}</span>
                      }
                    </dd>
                  }
                  @for (item of entry.items; track $index) {
                    <dd class="import-live-preview__item">
                      <span class="import-live-preview__item-label">
                        <ng-container *ngTemplateOutlet="labelText; context: {$implicit: item.label, suffix: ':'}"></ng-container>
                      </span>
                      @for (value of item.values; track $index) {
                        <span class="import-live-preview__item-value">
                          <ng-container *ngTemplateOutlet="valueText; context: {$implicit: value}"></ng-container>
                        </span>
                      }
                      @if (item.detail) {
                        <span class="import-live-preview__item-detail">· {{ item.detail }}</span>
                      }
                      @for (value of item.values; track $index) {
                        @if (value.path) {
                          <span class="import-live-preview__path">{{ value.path }}</span>
                        }
                      }
                    </dd>
                  }
                </div>
              }
            </dl>
          </section>
        }

        @if (preview.empty.length) {
          <div class="import-live-preview__empty">
            <span class="bold">{{ 'TRANS__IMPORT__LIVE_PREVIEW__EMPTY_IN_ROW' | translate }}</span>
            <span class="import-live-preview__empty-labels">
              @for (label of preview.empty; track $index) {
                <span class="import-live-preview__empty-label">
                  <ng-container *ngTemplateOutlet="labelText; context: {$implicit: label}"></ng-container>
                </span>
              }
            </span>
          </div>
        }
      }
    </div>
  } @else if (!store.livePreviewPending && !store.livePreviewError && !heldBack.length) {
    <p class="import-live-preview__hint">{{ 'TRANS__IMPORT__LIVE_PREVIEW__NOTHING_MAPPED' | translate }}</p>
  }

  <div class="import-live-preview__foot">
    @if (store.livePreviewNextWarningRow; as next) {
      <button
        mat-button
        type="button"
        class="import-live-preview__next-warning"
        (click)="store.setLivePreviewRow(next)"
      >
        <mat-icon>warning_amber</mat-icon>
        {{ 'TRANS__IMPORT__LIVE_PREVIEW__NEXT_WARNING' | translate: {row: next} }}
      </button>
    }
    <p class="import-live-preview__hint">{{ 'TRANS__IMPORT__LIVE_PREVIEW__NOTE' | translate }}</p>
  </div>
</section>

<!-- `suffix` follows the label with no space between. -->
<ng-template #labelText let-label let-suffix="suffix">
  @if (label.key) {
    {{ label.key | translate }}@if (label.index) { {{ label.index }}}{{ suffix }}
  } @else {
    {{ label.text }}{{ suffix }}
  }
</ng-template>

<!-- A value; a KulturNav objectid as a link to the Primus record holding
     it, else to its page, with the full objectid on hover. Either opens in
     a new tab, so the import stays open; a visually hidden hint after the
     icon puts that in the link's accessible name. No whitespace around the
     text: a value keeps its lines. -->
<ng-template #valueText let-value>
  @if (value.kulturnav; as ref) {
    @if (ref.artifact_id) {
      <a
        class="import-live-preview__link"
        target="_blank"
        rel="noopener noreferrer"
        [routerLink]="['/artifact', ref.artifact_id]"
        [matTooltip]="ref.uuid"
      ><span class="import-live-preview__link-text">{{ value.text }}</span><mat-icon
          class="import-live-preview__link-icon"
          aria-hidden="true"
        >open_in_new</mat-icon><span class="cdk-visually-hidden"> {{ 'TRANS__IMPORT__LIVE_PREVIEW__OPENS_IN_NEW_TAB' | translate }}</span></a>
    } @else {
      <a
        class="import-live-preview__link"
        target="_blank"
        rel="noopener noreferrer"
        [href]="ref.url"
        [matTooltip]="ref.uuid"
      ><span class="import-live-preview__link-text">{{ value.text }}</span><mat-icon
          class="import-live-preview__link-icon"
          aria-hidden="true"
        >open_in_new</mat-icon><span class="cdk-visually-hidden"> {{ 'TRANS__IMPORT__LIVE_PREVIEW__OPENS_IN_NEW_TAB' | translate }}</span></a>
    }
  } @else {
    <ng-container>{{ value.text }}</ng-container>
  }
</ng-template>
